import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Tag,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Spin,
  Alert,
  Table,
  Button,
  Tooltip,
} from "antd";
import {
  DollarOutlined,
  NumberOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  BankOutlined,
  WalletOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import axios from "axios";
import withRoleAccess from "../../hoc/withRoleAccess";
import dayjs from "dayjs";
import StudentReceiptModal from "./StudentReceiptModal";

const { Text } = Typography;

const AdminStudentBilling = ({ studentId }) => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [error, setError] = useState(null);
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [receiptDataMap, setReceiptDataMap] = useState({});

  useEffect(() => {
    const fetchAllBillingData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch registrations, payment summaries, sales, and receipt-ready
        // payment records in parallel
        const [regRes, summaryRes, salesRes, receiptRes] = await Promise.all([
          axios.get(
            `${API_URL}/student-registrations/user/registrations/${studentId}`,
            { headers }
          ),
          axios.get(
            `${API_URL}/student-registrations/payments/${studentId}/summary`,
            { headers }
          ),
          axios.get(
            `${API_URL}/student-registrations/student-sales/${studentId}`,
            { headers }
          ),
          axios.get(`${API_URL}/students/${studentId}/payments`, { headers }),
        ]);

        // Build receipt data map (keyed by payment_id) with fully-populated
        // records for the receipt modal (classes, student info, totals, etc.)
        const receiptMap = {};
        (receiptRes.data || []).forEach((rec) => {
          const parsedClasses =
            typeof rec.classes === "string"
              ? (() => {
                  try {
                    return JSON.parse(rec.classes || "[]");
                  } catch {
                    return [];
                  }
                })()
              : Array.isArray(rec.classes)
              ? rec.classes
              : [];
          receiptMap[rec.payment_id] = { ...rec, classes: parsedClasses };
        });
        setReceiptDataMap(receiptMap);

        // Build payment summary map
        const summaryMap = {};
        (summaryRes.data.summaries || []).forEach((s) => {
          summaryMap[s.batch_id] = {
            total_paid: Number(s.total_paid || 0),
            payment_count: s.payment_count,
          };
        });

        // Fetch payment details for each registration
        const regs = regRes.data.registrations || [];
        const paymentDetailsPromises = regs.map(async (reg) => {
          if (reg.batch_id) {
            try {
              const detailRes = await axios.get(
                `${API_URL}/student-registrations/payments/batch/${reg.batch_id}`,
                { headers }
              );
              return { batch_id: reg.batch_id, payments: detailRes.data.payments || [] };
            } catch {
              return { batch_id: reg.batch_id, payments: [] };
            }
          }
          return { batch_id: reg.batch_id, payments: [] };
        });

        const paymentDetailsResults = await Promise.all(paymentDetailsPromises);
        const paymentDetailsMap = {};
        paymentDetailsResults.forEach((result) => {
          paymentDetailsMap[result.batch_id] = result.payments;
        });

        // Transform registrations into billing records
        const registrationBillings = [];
        regs.forEach((reg) => {
          const payments = paymentDetailsMap[reg.batch_id] || [];
          if (payments.length > 0) {
            payments.forEach((payment) => {
              registrationBillings.push({
                id: `reg-${reg.registration_id}-${payment.payment_id}`,
                payment_id: payment.payment_id,
                date: payment.payment_date,
                class_code: reg.class_code,
                total_price: Number(reg.total_price || 0),
                discount: reg.discount || 0,
                vat: reg.vat || 0,
                amount_paid: Number(payment.pay_amount || 0),
                remaining_balance: Number(payment.remaining_balance || 0),
                payment_method: payment.payment_method,
                receipt_no: payment.receipt_no,
                memo: payment.memo,
              });
            });
          } else {
            // Registration with no payments yet
            registrationBillings.push({
              id: `reg-${reg.registration_id}`,
              date: reg.created_at || reg.start_date,
              class_code: reg.class_code,
              total_price: Number(reg.total_price || 0),
              discount: reg.discount || 0,
              vat: reg.vat || 0,
              amount_paid: 0,
              remaining_balance: Number(reg.total_price || 0),
              payment_method: null,
              receipt_no: null,
              memo: null,
            });
          }
        });

        // Transform sales into billing records
        const sales = salesRes.data.sales || [];
        const salesBillings = sales.map((sale) => ({
          id: `sale-${sale.transaction_id}`,
          date: sale.transaction_date,
          items: sale.items || [],
          description: sale.items?.map((i) => i.item_name).join(", ") || "Items",
          total_price: Number(sale.final_amount || 0),
          discount: Number(sale.discount || 0),
          vat: Number(sale.vat || 0),
          amount_paid: Number(sale.final_amount || 0),
          remaining_balance: 0,
          payment_method: sale.payment_method,
          document_no: sale.document_no,
          note: sale.note,
          branch: sale.branch,
        }));

        // Sort by date (newest first)
        registrationBillings.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());
        salesBillings.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());

        setRegistrationData(registrationBillings);
        setSalesData(salesBillings);
      } catch (err) {
        console.error("Error fetching billing data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (studentId) {
      fetchAllBillingData();
    }
  }, [API_URL, studentId]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const allData = [...registrationData, ...salesData];
    
    if (allData.length === 0) {
      return {
        totalPaid: 0,
        totalRemaining: 0,
        totalTransactions: 0,
        lastPayment: null,
      };
    }

    const totalPaid = allData.reduce(
      (sum, item) => sum + (item.amount_paid || 0),
      0
    );

    // Deduplicate remaining balance by class_code for registrations
    const latestByClass = {};
    registrationData.forEach((item) => {
      const key = item.class_code;
      const current = latestByClass[key];
      const newTime = new Date(item.date);
      if (!current || newTime > new Date(current.date)) {
        latestByClass[key] = item;
      }
    });

    const totalRemaining = Object.values(latestByClass).reduce((sum, item) => {
      const remaining = item.remaining_balance || 0;
      return remaining > 0 ? sum + remaining : sum;
    }, 0);

    const totalTransactions = allData.length;
    
    // Find last payment
    const sortedAll = [...allData].sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());
    const lastPayment = sortedAll[0] || null;

    return {
      totalPaid,
      totalRemaining,
      totalTransactions,
      lastPayment,
    };
  }, [registrationData, salesData]);

  const handleViewReceipt = (record) => {
    const richRecord = receiptDataMap[record.payment_id];
    setSelectedPayment(richRecord || record);
    setReceiptModalVisible(true);
  };

  // Payment method helpers
  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case "cash":
        return <WalletOutlined />;
      case "credit_card":
      case "credit card":
      case "card":
        return <CreditCardOutlined />;
      case "bank_transfer":
      case "bank transfer":
        return <BankOutlined />;
      default:
        return <DollarOutlined />;
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method?.toLowerCase()) {
      case "cash":
        return "green";
      case "credit_card":
      case "credit card":
      case "card":
        return "purple";
      case "bank_transfer":
      case "bank transfer":
        return "blue";
      case "cheque":
        return "orange";
      default:
        return "default";
    }
  };

  const formatPaymentMethod = (method) => {
    if (!method) return "N/A";
    return method.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Registration table columns
  const registrationColumns = [
    {
      title: "Receipt No.",
      dataIndex: "receipt_no",
      key: "receipt_no",
      width: 120,
      render: (text, record) =>
        record.payment_id ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => handleViewReceipt(record)}>
            {text || "View Receipt"}
          </Button>
        ) : (
          <Text>{text || "-"}</Text>
        ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 140,
      render: (date) => (
        <Space direction="vertical" size={0}>
          <Text strong>{date ? dayjs(date).format("MMM DD, YYYY") : "-"}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {date ? dayjs(date).format("HH:mm") : ""}
          </Text>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
{
  title: "Course",
  dataIndex: "class_code",
  key: "class_code",
  width: 150,
  render: (text) => (
    <Tag color="blue" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
      {text || "-"}
    </Tag>
  ),
},
    {
      title: "Total Price",
      dataIndex: "total_price",
      key: "total_price",
      width: 120,
      align: "right",
      render: (price) => <Text>฿{(price || 0).toLocaleString()}</Text>,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      width: 90,
      align: "right",
      render: (discount) => (
        <Text style={{ color: "#ff4d4f" }}>
          -฿{Number(discount || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "VAT",
      dataIndex: "vat",
      key: "vat",
      width: 70,
      align: "right",
      render: (vat) => <Text>{vat || 0}%</Text>,
    },
    {
      title: "Amount Paid",
      dataIndex: "amount_paid",
      key: "amount_paid",
      width: 120,
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: "#52c41a" }}>
          ฿{(amount || 0).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => (a.amount_paid || 0) - (b.amount_paid || 0),
    },
    {
      title: "Remaining",
      dataIndex: "remaining_balance",
      key: "remaining_balance",
      width: 120,
      align: "right",
      render: (balance) => {
        const amount = balance || 0;
        return (
          <Text style={{ color: amount > 0 ? "#ff4d4f" : "#52c41a" }}>
            ฿{amount.toLocaleString()}
          </Text>
        );
      },
      sorter: (a, b) => (a.remaining_balance || 0) - (b.remaining_balance || 0),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 140,
      render: (method) => (
        <Tag
          color={getPaymentMethodColor(method)}
          icon={getPaymentMethodIcon(method)}
        >
          {formatPaymentMethod(method)}
        </Tag>
      ),
      filters: [
        { text: "Cash", value: "cash" },
        { text: "Credit Card", value: "credit_card" },
        { text: "Bank Transfer", value: "bank_transfer" },
        { text: "Cheque", value: "cheque" },
      ],
      onFilter: (value, record) =>
        record.payment_method?.toLowerCase().includes(value),
    },
    {
      title: "Memo",
      dataIndex: "memo",
      key: "memo",
      width: 150,
      ellipsis: true,
      render: (memo) => <Text type="secondary">{memo || "-"}</Text>,
    },
  ];

  // Sales table columns
  const salesColumns = [
    {
      title: "Document No.",
      dataIndex: "document_no",
      key: "document_no",
      width: 120,
      render: (text) => <Text>{text || "-"}</Text>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 140,
      render: (date) => (
        <Space direction="vertical" size={0}>
          <Text strong>{date ? dayjs(date).format("MMM DD, YYYY") : "-"}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {date ? dayjs(date).format("HH:mm") : ""}
          </Text>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
  title: "Items",
  dataIndex: "items",
  key: "items",
  width: 200,
  render: (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return <Text type="secondary">N/A</Text>;
    }

    return (
      <Space direction="vertical" size={0}>
        {items.slice(0, 2).map((item, idx) => (
          <Text key={idx} style={{ fontSize: "12px" }}>
            
            {/* ✅ Product ID in front */}
            {item.product_id && (
              <Text
                strong
                style={{ marginRight: 4, color: "#595959" }}
              >
                {item.product_id}
              </Text>
            )}

            <Tag color="blue" style={{ marginRight: 4 }}>
              {item.item_name}
            </Tag>

            x{item.quantity}
          </Text>
        ))}

        {items.length > 2 && (
          <Tooltip
            title={items
              .slice(2)
              .map((i) => `${i.product_id ? `${i.product_id} ` : ""}${i.item_name} x${i.quantity}`)
              .join(", ")}
          >
            <Text type="secondary" style={{ fontSize: "11px", cursor: "help" }}>
              +{items.length - 2} more items
            </Text>
          </Tooltip>
        )}
      </Space>
    );
  },
},
    {
      title: "Total",
      dataIndex: "total_price",
      key: "total_price",
      width: 120,
      align: "right",
      render: (price) => <Text>฿{(price || 0).toLocaleString()}</Text>,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      width: 100,
      align: "right",
      render: (discount) => (
        <Text style={{ color: "#ff4d4f" }}>
          -฿{(discount || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "VAT",
      dataIndex: "vat",
      key: "vat",
      width: 80,
      align: "right",
      render: (vat) => <Text>฿{(vat || 0).toLocaleString()}</Text>,
    },
    {
      title: "Amount Paid",
      dataIndex: "amount_paid",
      key: "amount_paid",
      width: 120,
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: "#52c41a" }}>
          ฿{(amount || 0).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => (a.amount_paid || 0) - (b.amount_paid || 0),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 140,
      render: (method) => (
        <Tag
          color={getPaymentMethodColor(method)}
          icon={getPaymentMethodIcon(method)}
        >
          {formatPaymentMethod(method)}
        </Tag>
      ),
      filters: [
        { text: "Cash", value: "cash" },
        { text: "Credit Card", value: "credit_card" },
        { text: "Bank Transfer", value: "bank_transfer" },
        { text: "Cheque", value: "cheque" },
      ],
      onFilter: (value, record) =>
        record.payment_method?.toLowerCase().includes(value),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: 150,
      ellipsis: true,
      render: (note) => <Text type="secondary">{note || "-"}</Text>,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: 10 }}>
          <Text type="secondary">Loading billing records...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Billing Data"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Paid"
              value={summaryStats.totalPaid}
              prefix="฿"
              valueStyle={{ color: "#52c41a" }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Outstanding Balance"
              value={summaryStats.totalRemaining}
              prefix="฿"
              valueStyle={{
                color: summaryStats.totalRemaining > 0 ? "#ff4d4f" : "#52c41a",
              }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Transactions"
              value={summaryStats.totalTransactions}
              prefix={<NumberOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Last Payment"
              value={
                summaryStats.lastPayment
                  ? dayjs(summaryStats.lastPayment.date).format("MMM DD, YYYY")
                  : "N/A"
              }
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Outstanding Balance Alert */}
      {summaryStats.totalRemaining > 0 && (
        <Alert
          message="Outstanding Balance"
          description={`This student has an outstanding balance of ฿${summaryStats.totalRemaining.toLocaleString()}.`}
          type="warning"
          showIcon
          style={{ marginBottom: "24px" }}
        />
      )}

      {/* Registration Payments Table */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Course Registrations</span>
            <Tag color="blue">{registrationData.length}</Tag>
          </Space>
        }
        style={{ marginBottom: "24px" }}
      >
        <Table
          columns={registrationColumns}
          dataSource={registrationData}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} payments`,
          }}
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: "No registration payments found",
          }}
        />
      </Card>

      {/* Sales Table */}
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            <span>Sales / Inventory Purchases</span>
            <Tag color="gold">{salesData.length}</Tag>
          </Space>
        }
      >
        <Table
          columns={salesColumns}
          dataSource={salesData}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} transactions`,
          }}
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: "No sales transactions found",
          }}
        />
      </Card>

      {/* Receipt Modal */}
      <StudentReceiptModal
        visible={receiptModalVisible}
        onClose={() => setReceiptModalVisible(false)}
        paymentData={selectedPayment}
      />
    </div>
  );
};

export default withRoleAccess(AdminStudentBilling, ["admin", "superadmin"]);